import React, { useMemo } from 'react';
import { 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ComposedChart 
} from 'recharts';

/**
 * Chart colors configuration
 */
const CHART_COLORS = {
  actual: '#bebebe',
  calibrated: '#02B4CC',
  guidance: '#ff7300',
  grid: '#f5f5f5'
};

/**
 * Chart dimensions configuration
 */
const CHART_CONFIG = {
  width: 500,
  height: 300,
  barSize: 20
};

/**
 * Rating labels mapping
 */
const RATING_LABELS = ['A+', 'A', 'B+', 'B', 'C', 'D'];
const RATING_KEYS = ['APlus', 'A', 'BPlus', 'B', 'C', 'D'];

/**
 * Custom shape for bars with minimum visible height
 */
const CustomBar = (props) => {
  const { fill, x, y, width, height, value } = props;
  const minHeight = 5; // Minimum visible height in pixels
  
  // If value is 0, don't render anything
  if (value === 0) return null;
  
  // If height is too small but value exists, use minimum height
  const adjustedHeight = height < minHeight && value > 0 ? minHeight : height;
  const adjustedY = height < minHeight && value > 0 ? y - (minHeight - height) : y;
  
  return (
    <rect 
      x={x} 
      y={adjustedY} 
      width={width} 
      height={adjustedHeight} 
      fill={fill}
    />
  );
};

/**
 * Chart Component
 * Displays rating distribution comparison between Guidance, Actual, and Calibrated scores
 * 
 * @param {Object} ratingQuota - Guidance quota for each rating
 * @param {Object} actualScore - Actual scores for each rating
 * @param {Object} calibratedScore - Calibrated scores for each rating
 */
const Chart = ({ ratingQuota, actualScore, calibratedScore }) => {
  // Memoize chart data to avoid recalculation on every render
  const chartData = useMemo(() => {
    return RATING_LABELS.map((label, index) => {
      const key = RATING_KEYS[index];
      return {
        name: label,
        Guidance: ratingQuota?.[key] || 0,
        Actual: actualScore?.[key] || 0,
        Calibrated: calibratedScore?.[key] || 0
      };
    });
  }, [ratingQuota, actualScore, calibratedScore]);

  // Calculate max value for Y-axis domain
  const maxValue = useMemo(() => {
    const allValues = chartData.flatMap(d => [d.Guidance, d.Actual, d.Calibrated]);
    return Math.max(...allValues, 10); // Minimum 10 to ensure small values are visible
  }, [chartData]);

  return (
    <div className="chart-container">
      <ComposedChart 
        width={CHART_CONFIG.width} 
        height={CHART_CONFIG.height} 
        data={chartData}
      >
        <CartesianGrid stroke={CHART_COLORS.grid} />
        <XAxis dataKey="name" />
        <YAxis 
          domain={[0, maxValue]}
          allowDataOverflow={false}
          scale="linear"
          tickCount={8}
        />
        <Tooltip />
        <Legend />
        <Bar 
          dataKey="Actual" 
          barSize={CHART_CONFIG.barSize} 
          fill={CHART_COLORS.actual}
          name="Actual Score"
          shape={<CustomBar />}
        />
        <Bar 
          dataKey="Calibrated" 
          barSize={CHART_CONFIG.barSize} 
          fill={CHART_COLORS.calibrated}
          name="Calibrated Score"
          shape={<CustomBar />}
        />
        <Line 
          type="monotone" 
          dataKey="Guidance" 
          stroke={CHART_COLORS.guidance}
          strokeWidth={2}
          name="Guidance Quota"
        />
      </ComposedChart>
    </div>
  );
};

export default Chart;
